/** @jsx jsx */
import { ClassNames, jsx } from '@emotion/react';
import { SupportedLocale } from '@sprinx/knihovna-types';
import Container from 'components/Container';
import { PageHeadline } from 'components/Page';
import ProductCard from 'components/ProductCard';
import ProductListCarousel from 'components/ProductListCarousel';
import TextFormattedReplace from 'components/TextFormattedReplace';
import ExpandIcon from 'icons/ExpandIcon';
import React from 'react';
import Zoom from 'react-reveal/Zoom';
import translate from 'translate';
import { Product } from 'types/Product';

const BrandPageBrands: React.FC<{ locale: SupportedLocale; products: Product[] }> = ({ products, locale }) => {
  const t = translate(locale);
  return (
    <Container>
      <ClassNames>
        {({ theme }) => (
          <div css={{ display: 'flex', flexDirection: 'column', flexGrow: 1, padding: theme.spacing(2, 0) }}>
            <PageHeadline css={{ marginBottom: theme.spacing(2) }}>{t('brands.title')}</PageHeadline>
            <div
              css={{
                '& > div': {
                  margin: theme.spacing(2, 0),
                },
                '@media(min-width: 600px)': {
                  '& > div': {
                    margin: theme.spacing(2),
                  },
                },
                '@media(min-width: 960px)': {
                  alignItems: 'stretch',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                },
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {(products || []).map((product, idx) => (
                <Zoom key={idx.toString()} delay={300}>
                  <ProductBrandCard locale={locale} product={product} key={idx.toString()} id={idx.toString()} />
                </Zoom>
              ))}
            </div>
          </div>
        )}
      </ClassNames>
    </Container>
  );
};

export default BrandPageBrands;

BrandPageBrands.displayName = 'BrandPageBrands';

const ProductBrandCard: React.FC<{ id?: string; locale: SupportedLocale; product: Product }> = ({
  locale,
  product,
  id,
}) => {
  const [description, setDescription] = React.useState<string>('');
  const t = translate(locale);

  React.useEffect(() => {
    if (product.description.length > 300) {
      setDescription(`${product.description.slice(0, 300)}...`);
    }
  }, [product.description]);

  const handleCollapse = React.useCallback(() => {
    if (description.length > 303) {
      setDescription((ps) => `${ps.slice(0, 300)}...`);
    } else setDescription(product.description);
  }, [description, product.description]);

  return (
    <ClassNames>
      {({ theme, css, cx }) => (
        <ProductCard
          css={{
            '@media(min-width: 600px)': { padding: theme.spacing(6) },
            height: '100%',
            maxWidth: 600,
            width: '100%',
          }}
          id={id}
        >
          <div css={{ alignItems: 'stretch', display: 'flex', flexDirection: 'column' }}>
            <img src={product.logo} alt={product.title} css={{ alignSelf: 'flex-start', height: 58 }} />
            <div
              css={{
                '@media(min-width: 600px)': {
                  alignItems: 'flex-start',
                  flexDirection: 'row',
                },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div
                css={(theme) => ({
                  '@media(max-width: 600px)': {
                    height: 'auto',
                    margin: '0 auto',
                  },
                  flexShrink: 0,
                  height: '100%',
                  margin: theme.spacing(2, 0),
                  width: 120,
                })}
              >
                <ProductListCarousel>
                  {(product.images || []).map((i, idx) => (
                    <div
                      key={idx.toString()}
                      css={{
                        display: 'flex',
                        Height: 360,
                        justifyContent: 'center',
                        width: '100%',
                      }}
                    >
                      <img
                        src={i}
                        alt={product.title}
                        draggable={false}
                        css={(theme) => ({ flexShrink: 0, margin: theme.spacing(2, 0), maxWidth: '100%' })}
                      />
                    </div>
                  ))}
                </ProductListCarousel>
              </div>
              <div
                css={{
                  alignItems: 'flex-start',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: theme.spacing(3, 0, 3, 2),
                }}
              >
                <p
                  css={{
                    color: theme.palette.primary.main,
                    fontSize: 24,
                    fontWeight: theme.typography.fontWeightMedium,
                  }}
                >
                  {product.title}
                </p>
                <div
                  css={{
                    '@media(min-width: 600px)': { maxWidth: 270, minHeight: 166 },
                    color: theme.palette.text.secondary,
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: 14,
                    margin: theme.spacing(3, 0, 2, 0),
                    textAlign: 'justify',
                  }}
                >
                  <TextFormattedReplace
                    replaces={{
                      // eslint-disable-next-line react/display-name
                      bold: () => <span css={{ fontWeight: 'bold' }}>{product.descriptionBold}</span>,
                    }}
                  >
                    {description}
                  </TextFormattedReplace>
                  <button
                    onClick={handleCollapse}
                    aria-label={product.title}
                    css={{
                      '&:hover': {
                        background: theme.palette.grey[300],
                      },
                      alignItems: 'center',
                      alignSelf: 'flex-end',
                      background: theme.palette.common.white,
                      border: 'none',
                      borderRadius: 100,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      outline: 'none',
                      padding: theme.spacing(1),
                      transition: 'all .2s ease',
                    }}
                  >
                    <ExpandIcon
                      className={cx(
                        css({
                          transform: description.length > 303 ? 'rotate(180deg)' : 'unset',
                        }),
                      )}
                    />
                  </button>
                </div>
                {product.path && (
                  <a
                    href={product.path}
                    target='_blank'
                    rel='noreferrer'
                    css={{
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                      color: theme.palette.primary.main,
                      textDecoration: 'none',
                      transition: 'all .2s ease',
                    }}
                  >
                    {t('brands.goToWebsite')}
                  </a>
                )}
              </div>
            </div>
          </div>
        </ProductCard>
      )}
    </ClassNames>
  );
};

ProductBrandCard.displayName = 'ProductBrandCard';
